import { useState } from 'react';
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import {
  useLazyGetAdminOrganizationArtifactDownloadUrlQuery,
  useLazyGetArtifactDownloadUrlQuery,
} from '@features/patient-artifacts/store/api';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import type { IntakeHubDocumentInventoryItemResponse } from '@store/generated/intake-hub';
import { useSnackbar } from 'notistack';

import {
  useDownloadAdminIntakeHubDocumentsArchiveMutation,
  useDownloadIntakeHubDocumentsArchiveMutation,
} from '../store/api';

import { intakeDocumentInventoryItemToDetail } from './intakeDocumentDetail';
import { IntakeDocumentDetailDialog } from './IntakeDocumentDetailDialog';
import type { IntakeDocumentAccess } from './IntakeItemDetailsDialog';

interface IntakeDocumentInventorySectionProps {
  documents: IntakeHubDocumentInventoryItemResponse[];
  documentAccess?: IntakeDocumentAccess;
}

function humanize(value?: string | null): string {
  if (!value) return 'Document';
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, char => char.toUpperCase());
}

function formatDate(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function IntakeDocumentInventorySection({
  documents,
  documentAccess = { scope: 'provider' },
}: IntakeDocumentInventorySectionProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedDocument, setSelectedDocument] =
    useState<IntakeHubDocumentInventoryItemResponse | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [fetchProviderDownloadUrl] = useLazyGetArtifactDownloadUrlQuery();
  const [fetchAdminDownloadUrl] = useLazyGetAdminOrganizationArtifactDownloadUrlQuery();
  const [downloadProviderArchive, { isLoading: isProviderArchiveDownloading }] =
    useDownloadIntakeHubDocumentsArchiveMutation();
  const [downloadAdminArchive, { isLoading: isAdminArchiveDownloading }] =
    useDownloadAdminIntakeHubDocumentsArchiveMutation();
  const isArchiveDownloading = isProviderArchiveDownloading || isAdminArchiveDownloading;

  const fetchDocumentUrl = async (sourceArtifactId?: string | null): Promise<string | null> => {
    if (!sourceArtifactId) {
      enqueueSnackbar('This document does not have an original upload attached yet.', {
        variant: 'info',
      });
      return null;
    }

    try {
      const download =
        documentAccess.scope === 'admin'
          ? await fetchAdminDownloadUrl({
              organizationId: documentAccess.organizationId,
              artifactId: sourceArtifactId,
            }).unwrap()
          : await fetchProviderDownloadUrl({ artifactId: sourceArtifactId }).unwrap();
      return download.url;
    } catch (error) {
      console.error('Failed to open intake document', error);
      enqueueSnackbar('Could not open that document yet.', { variant: 'error' });
      return null;
    }
  };

  const handleOpenDetails = async (documentItem: IntakeHubDocumentInventoryItemResponse) => {
    setSelectedDocument(documentItem);
    setPreviewUrl(null);
    const sourceArtifactId = documentItem.document_reference.source_artifact_id;
    if (!sourceArtifactId) return;

    setIsPreviewLoading(true);
    try {
      const url = await fetchDocumentUrl(sourceArtifactId);
      setPreviewUrl(url);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleOpenInNewTab = async (sourceArtifactId?: string | null) => {
    const url = await fetchDocumentUrl(sourceArtifactId);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadAll = async () => {
    try {
      const download =
        documentAccess.scope === 'admin'
          ? await downloadAdminArchive({
              organizationId: documentAccess.organizationId,
              userId: documentAccess.userId,
            }).unwrap()
          : await downloadProviderArchive(null).unwrap();
      const url = URL.createObjectURL(download.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = download.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download intake documents', error);
      enqueueSnackbar('Could not download the document archive yet.', { variant: 'error' });
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 1.75, sm: 2 },
        borderRadius: '8px',
        bgcolor: ELIG_TOKENS.card,
        border: `1px solid ${ELIG_TOKENS.lineStrong}`,
        minHeight: 0,
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between' }}
        >
          <Box>
            <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 850 }}>
              Documents on file
            </Typography>
            <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkSoft }}>
              Uploaded documents and the information Intake Hub has pulled from them
            </Typography>
          </Box>
          {documents.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              startIcon={
                isArchiveDownloading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <FileDownloadOutlinedIcon fontSize="small" />
                )
              }
              disabled={isArchiveDownloading}
              onClick={() => void handleDownloadAll()}
              sx={{
                color: ELIG_TOKENS.midnight,
                borderColor: ELIG_TOKENS.lineStrong,
                textTransform: 'none',
                fontWeight: 800,
                whiteSpace: 'nowrap',
              }}
            >
              Download all
            </Button>
          )}
        </Stack>

        {documents.length > 0 ? (
          <Stack spacing={0.75}>
            {documents.map(documentItem => {
              const doc = documentItem.document_reference;
              const fields = documentItem.extracted_fields ?? [];
              const linkedItems = documentItem.linked_items ?? [];
              const dateLabel = formatDate(doc.created_at);
              return (
                <Box
                  key={doc.document_reference_id}
                  sx={{
                    border: `1px solid ${ELIG_TOKENS.line}`,
                    borderRadius: '6px',
                    bgcolor: ELIG_TOKENS.card,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    transition: 'background-color 120ms ease, box-shadow 120ms ease',
                    '&:hover': {
                      bgcolor: 'rgba(28,30,133,0.025)',
                      boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)',
                    },
                  }}
                  onClick={() => void handleOpenDetails(documentItem)}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      justifyContent: 'space-between',
                      px: 1.25,
                      py: 1,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'flex-start', minWidth: 0, flex: 1 }}
                    >
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: ELIG_TOKENS.graySoft,
                          color: ELIG_TOKENS.midnight,
                          flexShrink: 0,
                        }}
                      >
                        <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: ELIG_TOKENS.ink, fontWeight: 800 }}
                        >
                          {doc.title || humanize(doc.doc_kind)}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.75}
                          useFlexGap
                          sx={{ flexWrap: 'wrap', mt: 0.35, alignItems: 'center' }}
                        >
                          {doc.doc_kind && <Chip size="small" label={humanize(doc.doc_kind)} />}
                          {doc.document_domain && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={humanize(doc.document_domain)}
                            />
                          )}
                          <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
                            {fields.length} field{fields.length === 1 ? '' : 's'} pulled
                          </Typography>
                          {linkedItems.length > 0 && (
                            <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
                              {linkedItems.length} matched item
                              {linkedItems.length === 1 ? '' : 's'}
                            </Typography>
                          )}
                          {dateLabel && (
                            <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
                              Uploaded {dateLabel}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    </Stack>

                    <Button
                      size="small"
                      variant="text"
                      endIcon={<OpenInNewOutlinedIcon fontSize="small" />}
                      onClick={event => {
                        event.stopPropagation();
                        void handleOpenDetails(documentItem);
                      }}
                      sx={{ color: ELIG_TOKENS.midnight, textTransform: 'none', fontWeight: 700 }}
                    >
                      View
                    </Button>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Box
            sx={{
              p: 1.25,
              borderRadius: '6px',
              bgcolor: ELIG_TOKENS.grayTint,
              border: `1px solid ${ELIG_TOKENS.line}`,
            }}
          >
            <Typography variant="body2" sx={{ color: ELIG_TOKENS.inkSoft }}>
              No intake documents are on file yet.
            </Typography>
          </Box>
        )}
      </Stack>

      <IntakeDocumentDetailDialog
        open={Boolean(selectedDocument)}
        document={selectedDocument ? intakeDocumentInventoryItemToDetail(selectedDocument) : null}
        previewUrl={previewUrl}
        isPreviewLoading={isPreviewLoading}
        onOpenFile={handleOpenInNewTab}
        onClose={() => {
          setSelectedDocument(null);
          setPreviewUrl(null);
        }}
      />
    </Box>
  );
}
